import { Box, Table, type BoxProps, type TableProps } from '@chakra-ui/react';
import { type ReactNode } from 'react';

/**
 * InsetTable — a small table inside a rounded outline on white, used inside
 * drawers, modals, and expanded feed rows (stock adjustment history, part
 * numbers, purchase order line items). Header matches the inventory list
 * table header (`InventoryListTable`); this adds the outline, row dividers,
 * and drops the last row's divider so it doesn't double the outline.
 */
export const INSET_TABLE_SX = {
	'th, td': { px: 3 },
	th: {
		py: 2.5,
		bg: 'gray.50',
		color: 'gray.500',
		fontSize: '11px',
		fontWeight: 'semibold',
		letterSpacing: 'wider',
		textTransform: 'uppercase',
		borderColor: 'gray.200',
	},
	td: { py: 3, borderColor: 'border.subtle' },
	'tbody tr:last-of-type td': { borderBottomWidth: 0 },
} as const;

export interface InsetTableProps extends TableProps {
	/** Rendered inside the outline, below the table (e.g. an "Add row" bar). */
	footer?: ReactNode;
	shellProps?: BoxProps;
}

export function InsetTable({ children, footer, shellProps, sx, ...tableProps }: InsetTableProps) {
	return (
		<Box
			borderWidth="1px"
			borderColor="border.default"
			borderRadius="xl"
			overflow="hidden"
			bg="white"
			{...shellProps}
		>
			<Table size="sm" variant="simple" sx={{ ...INSET_TABLE_SX, ...sx }} {...tableProps}>
				{children}
			</Table>
			{footer}
		</Box>
	);
}

export default InsetTable;
